import i18next from 'i18next'
import moment from '@e3dc-react/shell/Libs/moment'
import { AppLang, AppLangNewStandard, AppLangs } from '@e3dc-react/shell/State/App/AppModel'
import { CountryCodes } from '@e3dc-react/shell/Hooks/useCountryOptions'
import { languageShortNames } from '@e3dc-react/shell/Configuration/languages'
import { UnitTypes } from 'src/Libs/types'

export const getCurrentLanguage = (): AppLang => {
  const fullLanguage = i18next.language || ''
  const shortLanguage = fullLanguage.split('-')[0].toLowerCase()

  if (languageShortNames.includes(fullLanguage)) return fullLanguage as AppLang
  if (languageShortNames.includes(shortLanguage)) return shortLanguage as AppLang
  return AppLangs.German /* DE used as default language */
}

export const changeAppLanguage = (language: AppLang): void => {
  localStorage.setItem('appLanguage', language)
  i18next.changeLanguage(language)
  moment.locale(language)
}

/**
 * Parse a string into a number depending on the language.
 */
export const getNormalizedNumber = (input: string): number => {
  const language = getCurrentLanguage()

  const formatParts = new Intl.NumberFormat(language).formatToParts(12345.6)
  const thousandsSeparator = formatParts.find(part => part.type === 'group')?.value ?? ''
  const decimalSeparator = formatParts.find(part => part.type === 'decimal')?.value ?? '.'

  const thousandsSeparatorRegex = new RegExp(`\\${thousandsSeparator}`, 'g')
  const decimalSeparatorRegex = new RegExp(`\\${decimalSeparator}`)

  const normalizedInput = input.replace(thousandsSeparatorRegex, '').replace(decimalSeparatorRegex, '.')

  const parsedNumber = parseFloat(normalizedInput)
  return parsedNumber
}

export const getLocalizedNumber = (value: number | string, fractionDigits?: number, language?: AppLang, hideTrailingZeros?: boolean): string => {
  const maximumFractionDigits = fractionDigits ?? 2
  const minimumFractionDigits = fractionDigits ?? 0

  if (typeof value === 'string') {
    value = getNormalizedNumber(value)
  }

  return new Intl.NumberFormat(language ?? i18next.language, {
    style: 'decimal',
    maximumFractionDigits,
    minimumFractionDigits,
    trailingZeroDisplay: hideTrailingZeros ? 'stripIfInteger' : 'auto',
  }).format(Number(value))
}

export const formatValueWithUnit = (value: number | string, unit: UnitTypes): string => {
  const appLanguage = getCurrentLanguage()
  let valueToReturn = value

  const formattedValue = Intl.NumberFormat(appLanguage, {
    style: 'decimal',
    trailingZeroDisplay: 'stripIfInteger',
  }).format(Number(value))

  if (!isNaN(Number(value))) {
    valueToReturn = formattedValue
  }

  return `${valueToReturn} ${unit}`
}

/**
 * @param value to e.g. display 8 % provide 8 and not 0.08.
 */
export const getLocalizedPercentage = (value: number): string => {
  if (value !== 0 && value < 1) {
    console.warn(
      'This function does not expect percentages in form of floating values. For example, if you want to display 8% provide 8 and not 0.08'
    )
  }

  return getLocalizedNumber(value) + ' ' + UnitTypes.percentage
}

export const getLocalizedCurrency = (value: number, currency = 'EUR'): string => {
  return new Intl.NumberFormat(i18next.language, { style: 'currency', currency: currency }).format(value)
}

export const getInitialLanguageByCountry = (countryCode: CountryCodes | string): AppLang => {
  switch (countryCode) {
    case 'DEU':
    case 'AUT':
    case 'CHE':
      return 'de'
    case 'NLD':
      return 'nl'
    default:
      return 'en'
  }
}

/**
 * Limits the number of decimal places for a number.
 */
export const limitDecimalPlaces = (value: number, maxDecimalPlaces: number): number => {
  return Number(value.toFixed(maxDecimalPlaces))
}

/**
 * The BE team is trying to switch to the ISO-639 3 standard.
 * The function maps the current app language to the corresponding ISO-639 3 code to have backward compatibility between the two standards.
 * https://iso639-3.sil.org/code_tables/639/data
 */
export const formatToISO639_3 = (lang: AppLang): AppLangNewStandard => {
  const iso639_3Map: Record<AppLang, AppLangNewStandard> = {
    de: 'deu',
    en: 'eng',
    nl: 'nld',
    fr: 'fra',
    it: 'ita',
    pl: 'pol',
    pt: 'por',
    es: 'spa',
    sv: 'swe',
    el: 'ell',
    'gsw-FR': 'fra',
    'gsw-CH': 'deu',
  }
  return iso639_3Map[lang]
}
